// Pure builder: turns the guided concrete-quality answers into a human-readable
// [{ question, answer }] trail, captured at apply time so the pump operator can
// later see HOW the concrete class was concluded. Stored as JSON on
// pumppuRequest.betoniLaatuSelvitys; the decision tree never leaves this repo.

import { rasitusluokkaTree } from "src/utils/quote/rasitusluokat";
import { kayttokohdePresets } from "src/utils/quote/kayttokohdePresets";
import { kayttokohdeOptions, labelFor } from "src/utils/quote/quoteConstants";

/**
 * @param {string} kayttokohde - use-case slug (e.g. "lattia")
 * @param {Object} guidedAnswers - flat { [nodeId]: optionId } map (preset +
 *   user picks + postQuestion answer), as assembled by SpecGuideModal.handleApply
 * @param {(key: string) => string} t - this module has no React/hook access, so
 *   the caller's useT() result is threaded in explicitly (see src/i18n's shared
 *   procedure).
 * @returns {{ specMode: "guided", steps: Array<{question:string, answer:string}> }}
 */
export function buildBetoniLaatuSelvitys(kayttokohde, guidedAnswers = {}, t) {
    const tree = rasitusluokkaTree(t);
    const steps = [{ question: t("wizard.shared.kayttokohdeLabel"), answer: labelFor(kayttokohdeOptions(t), kayttokohde, kayttokohde) }];

    let nodeId = tree.start;
    const guard = new Set(); // cycle guard — defensive, the tree is a DAG
    while (nodeId && !guard.has(nodeId)) {
        guard.add(nodeId);
        const node = tree.nodes[nodeId];
        if (!node || node.terminal) break;
        const optionId = guidedAnswers[nodeId];
        if (optionId == null) break;
        const opt = node.options.find((o) => o.id === optionId);
        if (!opt) break;
        steps.push({ question: node.question, answer: opt.label });
        nodeId = opt.next;
    }

    const postQuestion = kayttokohdePresets(t)[kayttokohde]?.postQuestion ?? null;
    if (postQuestion) {
        const optionId = guidedAnswers[postQuestion.id];
        if (optionId != null) {
            const opt = postQuestion.options.find((o) => o.id === optionId);
            if (opt) steps.push({ question: postQuestion.question, answer: opt.label });
        }
    }

    return { specMode: "guided", steps };
}
